import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSEO } from "../utils/useSEO";
import { buildProductSlug } from "../utils/slugify";
import Modallogin from "../components/auth/Modallogin";
import {
  Box, Button, Container, Heading, Text, Flex,
  SimpleGrid, Icon, Spinner, HStack, Badge,
} from "@chakra-ui/react";
import {
  FaTshirt,
  FaUserTie,
  FaChild,
  FaDumbbell,
  FaHeart,
  FaCouch,
  FaGamepad,
  FaCamera,
  FaTag,
  FaCheckCircle,
  FaTruck,
  FaShieldAlt,
} from "react-icons/fa";
import ProductCard, { ProductCardSkeleton } from "../features/ads/ProductCards/ProductCard";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { useCategories } from "../contexts/CategoriesContext";
import { CLOTHING_TITLES } from "../components/common/Catagories";
import { trackEvent } from "../matomo";

const RED = "#a21b1b";
const RED_DARK = "#881616";

// Map category names → icon + colors
const CAT_STYLES = {
  "Femmes":                    { icon: FaTshirt,   bg: "#FCE4EC", color: "#a21b1b" },
  "Hommes":                    { icon: FaUserTie,  bg: "#E3F2FD", color: "#1565C0" },
  "Enfants":                   { icon: FaChild,    bg: "#FFF3E0", color: "#F57C00" },
  "Sport & Fitness":           { icon: FaDumbbell, bg: "#E8F5E9", color: "#2E7D32" },
  "Mariage & Soirée":          { icon: FaHeart,    bg: "#FFF0F6", color: "#a21b1b" },
  "Maison & Articles de maison":{ icon: FaCouch,   bg: "#E8EAF6", color: "#3949AB" },
  "Divertissement":            { icon: FaGamepad,  bg: "#F3E5F5", color: "#7B1FA2" },
  "Loisirs & Collection":      { icon: FaCamera,   bg: "#E0F7FA", color: "#00838F" },
};
const DEFAULT_CAT_STYLE = { icon: FaTag, bg: "#F5F5F5", color: "#757575" };


const PAGE_SIZE = 12;

function Home() {
  const navigate = useNavigate();
  const { t, dir } = useLang();

  useSEO({
    title: "9achna — Achat et Vente de Vêtements Seconde Main en Algérie | بيع وشراء الملابس",
    description: "9achna, le Vinted algérien : achat et vente de vêtements seconde main entre particuliers en Algérie. Vide-dressing femme, homme, enfant — petits prix dans toutes les wilayas (Alger, Oran, Constantine).",
    canonical: "https://9achna.com/",
    keywords: "achat vente vetements algerie, vetements seconde main algerie, vinted algerie, vide dressing algerie, vendre vetements algerie, robes algerie, caftan algerie, karakou, robe kabyle, robe soirée algerie, vetements femme algerie, vetements homme algerie, vetements enfant algerie, بيع وشراء الملابس, ملابس مستعملة الجزائر, قفطان جزائري, ملابس أطفال مستعملة",
    ogImage: "https://9achna.com/logo.png",
    // WebSite + Organization JSON-LD lives in client/public/index.html (single source, served pre-JS to all crawlers)
  });
  const { categories: allCategories } = useCategories();
  // Home affiche uniquement les pillars clothing (pivot Vinted Algerie).
  // Les autres restent accessibles via /catégorie directe + dropdown navbar.
  const categories = allCategories.filter((c) => CLOTHING_TITLES.includes(c.title));
  const [products,     setProducts]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [showLogin,    setShowLogin]    = useState(false);
  const [page,         setPage]         = useState(1);
  const [total,        setTotal]        = useState(0);
  const hasMore    = products.length < total;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Sentinel element watched by IntersectionObserver
  const sentinelRef = useRef(null);
  const loadingRef  = useRef(false); // prevent concurrent fetches

  const handleSell = (source = 'hero') => {
    trackEvent('Navigation', 'publier_clique', source);
    if (localStorage.getItem("authToken")) navigate("/sell");
    else setShowLogin(true);
  };

  const fetchProducts = useCallback(async (pageNum) => {
    const { data: res } = await axios.get(
      `${backendUrl}/getProducts?page=${pageNum}&limit=${PAGE_SIZE}`
    );
    setProducts((prev) =>
      pageNum === 1 ? res.data : [...prev, ...res.data]
    );
    setTotal(res.pagination.total);
  }, [backendUrl]);

  // Initial load + première visite + scroll depth
  useEffect(() => {
    fetchProducts(1)
      .catch(console.error)
      .finally(() => setLoading(false));

    if (!localStorage.getItem('9achna_visited')) {
      localStorage.setItem('9achna_visited', '1');
      trackEvent('Navigation', 'premiere_visite', 'home');
    }

    const reached = new Set();
    const onScroll = () => {
      const pct = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (pct >= 50 && !reached.has(50))  { reached.add(50);  trackEvent('Scroll', 'scroll_50',  'home'); }
      if (pct >= 90 && !reached.has(90))  { reached.add(90);  trackEvent('Scroll', 'scroll_90',  'home'); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [fetchProducts]);

  // Keep fresh refs so the IntersectionObserver callback never reads stale state
  const pageRef     = useRef(1);
  const totalRef    = useRef(0);
  const productsLen = useRef(0);

  useEffect(() => { pageRef.current  = page;  }, [page]);
  useEffect(() => { totalRef.current = total; }, [total]);
  useEffect(() => { productsLen.current = products.length; }, [products]);

  // Infinite scroll — fires when sentinel enters viewport
  useEffect(() => {
    if (loading) return; // wait for first load
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (!entry.isIntersecting) return;
        if (loadingRef.current) return;
        if (productsLen.current >= totalRef.current) return;

        loadingRef.current = true;
        setLoadingMore(true);
        const next = pageRef.current + 1;
        try {
          await fetchProducts(next);
          setPage(next);
        } catch { /* ignore */ }
        setLoadingMore(false);
        loadingRef.current = false;
      },
      { rootMargin: "300px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchProducts, loading]);

  return (
    <Box bg="#F5F5F5" dir={dir}>

      {/* ══════════════════════════════════════════════════════════
          HERO — style Vinted
      ══════════════════════════════════════════════════════════ */}
      <Box
        position="relative"
        minH={{ base: "360px", md: "460px" }}
        overflow="hidden"
        bg="gray.100"
      >
        {/* Abstract SVG background — thème 9achna rouge */}
        <Box
          position="absolute" inset={0}
          bgGradient="linear(135deg, #fdf2f2 0%, #fde8e8 40%, #f9c4c4 70%, #f0a0a0 100%)"
        />
        {/* Decorative SVG shapes */}
        <Box as="svg" position="absolute" inset={0} w="100%" h="100%"
          viewBox="0 0 1200 460" preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg" pointerEvents="none"
        >
          {/* Large soft circle — right */}
          <circle cx="950" cy="230" r="320" fill="#a21b1b" fillOpacity="0.08" />
          <circle cx="1100" cy="100" r="180" fill="#a21b1b" fillOpacity="0.06" />
          {/* Medium circle — bottom right */}
          <circle cx="850" cy="420" r="150" fill="#a21b1b" fillOpacity="0.07" />
          {/* Small accent circle */}
          <circle cx="680" cy="80" r="80" fill="#a21b1b" fillOpacity="0.05" />
          {/* Diagonal stripe */}
          <rect x="600" y="-50" width="60" height="600"
            rx="30" fill="#a21b1b" fillOpacity="0.04"
            transform="rotate(15 900 230)" />
          <rect x="750" y="-50" width="30" height="600"
            rx="15" fill="#a21b1b" fillOpacity="0.04"
            transform="rotate(15 900 230)" />
          {/* Hanger icon shape — decorative */}
          <path d="M920 160 Q960 120 1000 160 L1080 280 H840 Z"
            fill="#a21b1b" fillOpacity="0.04" />
        </Box>
        {/* Left fade to white so card text is always readable */}
        <Box
          position="absolute" inset={0}
          bgGradient={{
            base: "linear(to-b, white 0%, whiteAlpha.600 100%)",
            md:   "linear(to-r, white 35%, transparent 65%)",
          }}
        />

        <Container maxW="container.xl" position="relative" h="100%">
          <Flex
            align="center"
            justify={{ base: "flex-start", md: "center" }}
            minH={{ base: "360px", md: "460px" }}
            py={{ base: 10, md: 0 }}
          >
            {/* ── White card — centré desktop, pleine largeur mobile ── */}
            <Box
              bg="white"
              borderRadius="2xl"
              p={{ base: 6, md: 8 }}
              maxW={{ base: "100%", sm: "400px" }}
              boxShadow="0 8px 40px rgba(0,0,0,0.14)"
              w={{ base: "100%", sm: "auto" }}
            >
              <Badge
                bg="red.50" color={RED} fontSize="xs" fontWeight="700"
                px={3} py={1} borderRadius="full" mb={5}
                textTransform="none" letterSpacing="0.02em"
              >
                🇩🇿 {t("hero_badge")}
              </Badge>

              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "3xl" }}
                fontWeight="900"
                color="gray.900"
                lineHeight="1.15"
                mb={3}
              >
                {t("hero_title_line1")}<br />{t("hero_title_line2")}
              </Heading>

              <Text fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={6} lineHeight="1.7" fontWeight="400">
                {t("hero_subtitle")}{" "}
                <Box as="span" fontWeight="600" color="gray.800">{t("hero_subtitle_bold")}</Box>
              </Text>

              <Button
                onClick={handleSell}
                bg={RED} color="white"
                w="100%" size="lg" h="52px"
                borderRadius="xl" fontWeight="700" fontSize="md"
                _hover={{ bg: RED_DARK, transform: "translateY(-2px)", boxShadow: `0 8px 24px rgba(162,27,27,0.4)` }}
                transition="all 0.2s"
                mb={3}
              >
                {t("hero_cta_sell")}
              </Button>

              <Box textAlign="center" mb={5}>
                <Box
                  as={Link} to="/comment-ca-marche"
                  onClick={() => trackEvent('Navigation', 'comment_ca_marche_clique', 'hero')}
                  fontSize="sm" color={RED} fontWeight="600"
                  _hover={{ color: RED_DARK }}
                  display="inline-flex" alignItems="center" gap={1}
                >
                  {t("hero_cta_how")}
                </Box>
              </Box>

              {/* Trust micro-badges */}
              <HStack spacing={3} justify="center" flexWrap="wrap">
                {[
                  { icon: FaCheckCircle, key: "hero_trust_free" },
                  { icon: FaTruck,       key: "hero_trust_delivery" },
                  { icon: FaShieldAlt,   key: "hero_trust_secure" },
                ].map(({ icon, key }) => (
                  <HStack key={key} spacing={1}>
                    <Icon as={icon} boxSize={3} color={RED} />
                    <Text fontSize="11px" color="gray.500" fontWeight="600">{t(key)}</Text>
                  </HStack>
                ))}
              </HStack>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* ── Categories ──────────────────────────────────────────── */}
      <Box bg="white" boxShadow="sm">

        {/* Mobile: horizontal scrollable pills */}
        <Box display={{ base: "block", md: "none" }} overflowX="auto" px={3} py={3}
          sx={{ "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" }}>
          <Flex gap={2} w="max-content">
            {categories.map((cat, i) => {
              const style = CAT_STYLES[cat.title] || DEFAULT_CAT_STYLE;
              return (
                <Box key={cat.id || i} as={Link} to={`/${encodeURIComponent(cat.title)}`}
                  onClick={() => trackEvent('Navigation', 'categorie_cliquee', cat.title)}
                  textDecoration="none" flexShrink={0}>
                  <Flex align="center" gap={2} px={3} py={2}
                    borderRadius="full" border="1.5px solid" borderColor="gray.200"
                    bg="white" _hover={{ borderColor: RED, bg: "red.50" }}
                    transition="all 0.15s" whiteSpace="nowrap"
                  >
                    <Box w="24px" h="24px" borderRadius="full" bg={style.bg}
                      display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                      <Icon as={style.icon} boxSize={3} color={style.color} />
                    </Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.700">{cat.title}</Text>
                  </Flex>
                </Box>
              );
            })}
          </Flex>
        </Box>

        {/* Desktop: icon grid */}
        <Box display={{ base: "none", md: "block" }} py={8}>
          <Container maxW="container.xl">
            <Heading as="h2" fontSize="xl" fontWeight="700" color="#002F34" mb={6}>
              {t("categories_title")}
            </Heading>
            <SimpleGrid columns={5} spacing={4}>
              {categories.map((cat, i) => {
                const style = CAT_STYLES[cat.title] || DEFAULT_CAT_STYLE;
                return (
                  <Box key={cat.id || i} as={Link} to={`/${encodeURIComponent(cat.title)}`}
                    onClick={() => trackEvent('Navigation', 'categorie_cliquee', cat.title)}
                    display="flex" flexDirection="column" alignItems="center" gap={2}
                    textDecoration="none" role="group" cursor="pointer" py={1}>
                    <Box bg={style.bg} borderRadius="full" w="68px" h="68px"
                      display="flex" alignItems="center" justifyContent="center"
                      transition="all 0.2s"
                      _groupHover={{ transform: "scale(1.08)", boxShadow: "0 4px 14px rgba(0,0,0,0.13)" }}>
                      <Icon as={style.icon} boxSize={6} color={style.color} />
                    </Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.700" textAlign="center"
                      noOfLines={2} lineHeight="1.3" transition="color 0.2s" _groupHover={{ color: RED }}>
                      {cat.title}
                    </Text>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Container>
        </Box>
      </Box>

      {/* ── Products grid ─────────────────────────────────────── */}
      <Box bg="#F5F5F5" py={8} mt={2}>
        <Container maxW="container.xl">
          <Flex justify="space-between" align="center" mb={5}>
            <Heading
              as="h2"
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="700"
              color="#002F34"
            >
              {t("latest_ads")}
            </Heading>
            {total > 0 && (
              <Text fontSize="sm" color="gray.500" fontWeight="500">
                {total} {t("items_found")}
              </Text>
            )}
          </Flex>

          {loading ? (
            <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={{ base: 3, md: 4 }}>
              {Array.from({ length: PAGE_SIZE }).map((_, i) => <ProductCardSkeleton key={i} />)}
            </SimpleGrid>
          ) : products.length === 0 ? (
            <Box textAlign="center" py={16}>
              <Text color="gray.500" fontSize="lg">{t("no_ads")}</Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={{ base: 3, md: 4 }}>
              {products.map((product, index) => (
                <Link
                  key={product._id}
                  to={`/${buildProductSlug(product)}`}
                  style={{ textDecoration: "none" }}
                >
                  <ProductCard product={product} lcp={index === 0} />
                </Link>
              ))}
            </SimpleGrid>
          )}

          {/* Sentinel — IntersectionObserver watches this */}
          <Box ref={sentinelRef} h="1px" />

          {loadingMore && (
            <Flex justify="center" py={6}>
              <Spinner size="lg" color={RED} thickness="3px" speed="0.7s" />
            </Flex>
          )}
        </Container>
      </Box>

      <Modallogin staticModal={showLogin} setStaticModal={setShowLogin} toggleShow={() => setShowLogin(false)} />

      {/* ── Sell CTA ──────────────────────────────────────────── */}
      <Box bg={RED} py={{ base: 10, md: 12 }}>
        <Container maxW="container.md" textAlign="center">
          <Heading
            color="white"
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="700"
            mb={3}
          >
            {t("sell_cta_title")}
          </Heading>
          <Text color="whiteAlpha.900" mb={6} fontSize={{ base: "sm", md: "md" }}>
            {t("sell_cta_desc")}
          </Text>
          <Button
            onClick={() => handleSell('cta_bas')}
            bg="white"
            color={RED}
            borderRadius="md"
            fontWeight="700"
            size="lg"
            px={10}
            _hover={{ bg: "gray.100", transform: "translateY(-2px)", boxShadow: "lg" }}
            _active={{ transform: "translateY(0)" }}
            transition="all 0.2s"
          >
            {t("publish_ad")}
          </Button>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;
